import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CreditCard,
  Landmark,
  LayoutDashboard,
  QrCode,
  ShieldCheck,
  Building2,
  ArrowRight,
  Lock,
  Zap,
  Clock3,
} from "lucide-react";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// Same theme tokens as Work.jsx / Careers.jsx / Solutions.jsx / Contact.jsx.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const products = [
  {
    title: "Payment Solutions",
    description: "Fast, secure and scalable payment systems for modern businesses.",
    icon: CreditCard,
  },
  {
    title: "Digital Banking APIs",
    description: "Powerful APIs for banking, wallets, transfers and automation.",
    icon: Landmark,
  },
  {
    title: "Business Dashboard",
    description: "Real-time dashboards for tracking transactions and analytics.",
    icon: LayoutDashboard,
  },
  {
    title: "UPI Integration",
    description: "Seamless UPI and instant payment integration for apps.",
    icon: QrCode,
  },
  {
    title: "KYC & Security",
    description: "Secure onboarding with automated KYC verification systems.",
    icon: ShieldCheck,
  },
  {
    title: "Enterprise SaaS",
    description: "Custom SaaS solutions built for large-scale enterprises.",
    icon: Building2,
  },
];

const whyChoose = [
  { icon: Zap, title: "Scalable Architecture", desc: "Built to grow with your business, from first users to millions." },
  { icon: Lock, title: "Secure & Encrypted", desc: "End-to-end encrypted infrastructure with enterprise-grade compliance." },
  { icon: Clock3, title: "24×7 Support", desc: "Dedicated technical support whenever your business needs it." },
];

const productItems = ["Payments", "Banking APIs", "Dashboards", "UPI", "KYC", "SaaS"];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

function ProductBand() {
  return (
    <div
      className="relative py-8 overflow-hidden border-y"
      style={{ backgroundColor: theme.darkBandDeep, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <style>{`
        @keyframes productsMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .products-marquee-track { animation: productsMarquee 20s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .products-marquee-track { animation: none; } }
      `}</style>
      <div className="flex w-max products-marquee-track">
        {[...productItems, ...productItems].map((item, i) => (
          <span key={i} className="px-10 text-lg font-bold whitespace-nowrap" style={{ color: "rgba(255,255,255,0.35)" }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "hex pulse" motif, distinct from every other
// page's hero animation. A honeycomb of hexagon outlines sits behind
// the copy; cells light up in a traveling wave sequence like a product
// / circuit assembly coming alive, plus a soft ambient glow.
// ---------------------------------------------------------------------
const HEX_W = 64;
const HEX_H = 74;
const hexCols = 11;
const hexRows = 6;

function hexPoints(cx, cy, r) {
  return Array.from({ length: 6 })
    .map((_, i) => {
      const angle = (Math.PI / 180) * (60 * i - 30);
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    })
    .join(" ");
}

function HexPulseField() {
  const hexes = [];
  for (let row = 0; row < hexRows; row++) {
    for (let col = 0; col < hexCols; col++) {
      const x = col * HEX_W * 0.75;
      const y = row * HEX_H + (col % 2 === 1 ? HEX_H / 2 : 0);
      const seq = (col + row * 2) % 10;
      hexes.push({ x, y, seq, gold: (col + row) % 5 === 0 });
    }
  }

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hex-glow" />
      <svg
        className="absolute inset-0 w-full h-full opacity-90"
        viewBox={`0 0 ${hexCols * HEX_W * 0.75} ${hexRows * HEX_H + HEX_H}`}
        preserveAspectRatio="xMidYMid slice"
      >
        {hexes.map((h, i) => (
          <polygon
            key={i}
            points={hexPoints(h.x + HEX_W / 2, h.y + HEX_H / 2, HEX_W / 2 - 3)}
            fill="none"
            stroke={h.gold ? "rgba(217,164,65,0.5)" : "rgba(230,57,70,0.4)"}
            strokeWidth="1"
            className="hex-cell"
            style={{ animationDelay: `${h.seq * 0.25}s` }}
          />
        ))}
      </svg>
      <span className="hex-dots" />
    </div>
  );
}

export default function Products() {
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .hex-glow {
          position: absolute; top: 40%; left: 50%; width: 40rem; height: 40rem;
          margin: -20rem 0 0 -20rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}28, transparent 68%);
          filter: blur(70px);
          animation: hexGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes hexGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }

        .hex-cell {
          opacity: 0.12;
          animation: hexCellPulse 3.5s ease-in-out infinite;
        }
        @keyframes hexCellPulse {
          0%, 100% { opacity: 0.12; }
          15% { opacity: 0.85; }
          35% { opacity: 0.12; }
        }

        .hex-dots {
          position: absolute; inset: -10px; opacity: 0.05;
          background-image: radial-gradient(#FFFFFF 1px, transparent 1px); background-size: 26px 26px;
          animation: hexDotsDrift 48s linear infinite;
        }
        @keyframes hexDotsDrift { from { background-position: 0 0; } to { background-position: -260px 260px; } }

        @media (prefers-reduced-motion: reduce) {
          .hex-glow, .hex-cell, .hex-dots { animation: none !important; }
        }

        .card-glow {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
      `}</style>

      {/* ================= HERO (dark, hex pulse field) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-28 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <HexPulseField />
        </motion.div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow center light>What We Build</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Our" className="text-white" />{" "}
              <span style={{ color: theme.accent }}>
                <SplitText text="Products" delay={0.2} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
            >
              We build scalable digital products and fintech-grade solutions designed to help businesses grow faster.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65, ease: EASE }}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <MagneticButton>
                
                <a  href="#products-grid"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Explore Products
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </a>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Talk to Us
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ProductBand />

      {/* ================= PRODUCTS GRID (light) ================= */}
      <section id="products-grid" className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Our Range</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Built for" /> <span style={{ color: theme.accent }}>every need</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {products.map((product) => {
              const Icon = product.icon;
              return (
                <motion.div
                  key={product.title}
                  variants={staggerItem}
                  whileHover={{ y: -8 }}
                  className="group relative p-7 rounded-2xl border overflow-hidden transition-colors duration-300"
                  style={{ backgroundColor: theme.surface, borderColor: theme.border }}
                >
                  <span className="card-glow" />
                  <div
                    className="relative w-14 h-14 flex items-center justify-center rounded-xl mb-6"
                    style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                  >
                    <Icon size={26} />
                  </div>
                  <h2 className="relative text-xl font-semibold" style={{ color: theme.ink }}>
                    {product.title}
                  </h2>
                  <p className="relative mt-3 leading-7" style={{ color: theme.inkMuted }}>
                    {product.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <RevealSection>
            <Eyebrow center light>Why Choose Us</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">
              <SplitText text="Reliable, secure," /> <span style={{ color: theme.accent }}>always on</span>
            </h2>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-14 grid md:grid-cols-3 gap-6 text-left"
          >
            {whyChoose.map((item) => (
              <motion.div
                key={item.title}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className="p-6 rounded-2xl border transition-colors duration-300"
                style={{ backgroundColor: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-5"
                  style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                >
                  <item.icon size={22} />
                </div>
                <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-white/50 leading-6">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Ready to build" />
            <span className="block">
              <SplitText text="something powerful?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Let's turn your idea into a scalable product.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Us
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}